import React, { useState, useEffect } from "react";

import { createPortal } from "react-dom";

import { FiSearch, FiCalendar, FiDownload, FiEye, FiEyeOff, FiFileText, FiX, FiAlertCircle, FiClock, FiShield, FiPaperclip, FiRefreshCw, FiUnlock, FiLock } from "react-icons/fi";

import "./staffcss/medicalappointments.css";

import { createPDF, createAppointmentSlipPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';

import { auth, db } from "../../firebase";

import { getDoc, doc, query, collection, onSnapshot, where, updateDoc, arrayRemove } from "firebase/firestore";

import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";

import { appointmentService } from "../../services/appointmentService";
import { notificationService } from "../../services/firestoreService";
import { ADMIN_ACCOUNT_ID } from "../../services/adminService";

import Banner from "./Banner";
import Portal from "../../components/Portal";

// Helper function to get badge color based on assistance reason
const getAssistanceBadgeColor = (reason) => {
  const colors = {
    minor: '#dbeafe',
    senior: '#fef3c7',
    pwd: '#d1fae5',
    nrnw: '#fce7f3',
    other: '#e5e7eb'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};

const getAssistanceBadgeTextColor = (reason) => {
  const colors = {
    minor: '#1e40af',
    senior: '#92400e',
    pwd: '#065f46',
    nrnw: '#9d174d',
    other: '#374151'
  };
  return colors[reason?.toLowerCase()] || colors.other;
};



const getStatusLabel = (status) => {

  const mapping = {

    'COMPLETED': 'Completed',

    'CANCELLED_BY_USER': 'Cancelled by User',

    'CANCELLED_BY_PRO': 'Cancelled by You',

  };

  return mapping[status] || status;

};



const getStatusBadgeClass = (status) => {

  return `ma-badge-${status}`;

};



// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting }) {
  const [selectedFormat, setSelectedFormat] = React.useState(null);

  React.useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}



const isAccessActive = (request, currentTime) => {

  if (!request || request.status !== 'ACTIVE') return false;

  const requesterId = request.professionalId || request.proId;
  if (!requesterId || requesterId !== auth.currentUser?.uid) return false;

  if (!request.expiresAt) return true;

  const expiryDate = request.expiresAt?.toDate
    ? request.expiresAt.toDate()
    : new Date(request.expiresAt);

  return !Number.isNaN(expiryDate.getTime()) && expiryDate > currentTime;

};

const formatAccessExpiry = (expiresAt) => {
  const expiryDate = expiresAt?.toDate ? expiresAt.toDate() : new Date(expiresAt);
  return expiryDate.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};



function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <Portal>
      <div
        className={`feedback-modal ${type}`}
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          padding: "12px 20px",
          borderRadius: "8px",
          color: "#fff",
          backgroundColor: type === "success" ? "#36C667" : "#e74c3c",
          boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
          zIndex: 10001,
          transition: "opacity 0.3s",
        }}
      >
        {message}
      </div>
    </Portal>
  );
}

const AdminRecords = ({ appointments, staffProfile, refreshAppointments, sidebarCollapsed, highlightState }) => {
  // Fetch appointments if not provided
  const [localAppointments, setLocalAppointments] = useState(appointments || []);
  const [isLoading, setIsLoading] = useState(!appointments);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [highlightedPatientUid, setHighlightedPatientUid] = useState(null);
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState(null);

  // Fetch appointments from Firebase if not provided as prop
  useEffect(() => {
    if (!appointments) {
      setIsLoading(true);
      const q = query(collection(db, "appointments"), where("status", "==", "COMPLETED"));
      const unsubscribe = onSnapshot(q, (snap) => {
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setLocalAppointments(data);
        setIsLoading(false);
        setIsRefreshing(false);
      }, (err) => {
        console.error("Error fetching appointments:", err);
        setIsLoading(false);
        setIsRefreshing(false);
      });
      return () => unsubscribe();
    }
  }, [appointments]);

  // Fetch staff profile if not provided
  const [localStaffProfile, setLocalStaffProfile] = useState(staffProfile);
  useEffect(() => {
    if (!staffProfile) {
      const fetchStaffProfile = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        try {
          const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
          if (userSnap.exists()) {
            setLocalStaffProfile(userSnap.data());
          }
        } catch (err) {
          console.error('Error fetching staff profile:', err);
        }
      };
      fetchStaffProfile();
    }
  }, [staffProfile]);

  // Use local or props
  const effectiveAppointments = appointments || localAppointments;
  const effectiveStaffProfile = staffProfile || localStaffProfile;

  // Handle highlight state from ProNotifications
  useEffect(() => {
    if (highlightState?.highlightPatientUid) {
      setHighlightedPatientUid(highlightState.highlightPatientUid);
      setHighlightedAppointmentId(highlightState.highlightAppointmentId || null);

      // Auto-clear highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedPatientUid(null);
        setHighlightedAppointmentId(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [highlightState]);

  // Handle click outside to clear highlight
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (highlightedPatientUid && !e.target.closest('tr.highlighted-row')) {
        setHighlightedPatientUid(null);
        setHighlightedAppointmentId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [highlightedPatientUid]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshKey(k => k + 1);
    if (refreshAppointments) {
      await refreshAppointments();
    }
    setIsRefreshing(false);
  };

  const [search, setSearch] = useState("");

  const [dateFrom, setDateFrom] = useState("");

  const [dateTo, setDateTo] = useState("");

  const [serviceFilter, setServiceFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const SERVICE_FILTER_TABS = [
    { label: "All", value: "" },
    { label: "Reproductive Health Consultation", value: "Reproductive Health Consultation" },
    { label: "STI Testing", value: "STI Testing / Screening" },
    { label: "Family Planning", value: "Family Planning / Contraceptive Services" },
  ];

  const [currentPage, setCurrentPage] = useState(1);

  const [sortConfig, setSortConfig] = useState(null);

  const itemsPerPage = 10;

  const [loading, setLoading] = useState(isLoading);



  const [viewing, setViewing] = useState(null);

  const [showAccessModal, setShowAccessModal] = useState(false);

  const [showViewDataModal, setShowViewDataModal] = useState(false);

  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [patientRecords, setPatientRecords] = useState([]);
  const [allPatientAppointments, setAllPatientAppointments] = useState([]);

  const [clinicalNote, setClinicalNote] = useState("");

  const [attachmentFiles, setAttachmentFiles] = useState([]);

  const [isUploading, setIsUploading] = useState(false);

  const [noteError, setNoteError] = useState("");

  const [existingNotes, setExistingNotes] = useState([]);

  const [ndaAgreed, setNdaAgreed] = useState(false);

  const [showAttachmentEditor, setShowAttachmentEditor] = useState(false);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const lastFetchedAppointmentId = React.useRef(null);
  const appointmentDataCache = React.useRef({});

  const [showBlockchainModal, setShowBlockchainModal] = useState(false);

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [selectedExportFormat, setSelectedExportFormat] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, type: 'default' });

  // Tooltip handlers
  const handleActionMouseEnter = (e, text, type = 'default') => {
    setTooltip({
      visible: true,
      text: text,
      x: e.clientX - 150,
      y: e.clientY,
      type: type
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0, type: 'default' });
  };

  const [pendingRequests, setPendingRequests] = useState({});

  const [requestsLoading, setRequestsLoading] = useState(true);

  const [patientInfo, setPatientInfo] = useState(null);

  const [isLoadingRecords, setIsLoadingRecords] = useState(false);

  const [accessDuration, setAccessDuration] = useState(1);

  const [staffId, setStaffId] = useState("");

  const [staffIdValid, setStaffIdValid] = useState(false);

  const [staffIdError, setStaffIdError] = useState("");

  const [staffPassword, setStaffPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [passwordError, setPasswordError] = useState("");

  const [bannerMessage, setBannerMessage] = useState("");

  const [bannerType, setBannerType] = useState("error");

  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Patient data for grouped view
  const [groupedPatients, setGroupedPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);



  // Get the authenticated user's Staff ID from staffProfile for local validation

  const actualStaffId = effectiveStaffProfile?.staffId ||
    effectiveStaffProfile?.adminId ||
    (effectiveStaffProfile?.accountType === "Admin" ? ADMIN_ACCOUNT_ID : "");

  const accessScope = 'admin';
  const getOwnAccessRequest = (appointment) => {
    const requests = Object.values(appointment?.dataAccessRequests || {});
    const matchesCurrentAdmin = (req) =>
      (req.professionalId === auth.currentUser?.uid || req.proId === auth.currentUser?.uid) &&
      (!req.accessScope || req.accessScope === accessScope);
    const ownRequests = requests.filter(matchesCurrentAdmin);
    return ownRequests.find(req => req.status === 'ACTIVE') ||
      ownRequests.find(req => req.status === 'PENDING') ||
      ownRequests[0] ||
      null;
  };



  // Keep 'viewing' in sync with real-time appointments prop

  // eslint-disable-next-line react-hooks/exhaustive-deps

  React.useEffect(() => {

    if (viewing) {

      const updated = effectiveAppointments.find(a => a.id === viewing.id);

      if (updated) {

        setViewing({ ...updated, dataAccessRequest: getOwnAccessRequest(updated) });

      }

    }

  }, [effectiveAppointments, viewing?.id]);



  // Fetch latest appointment data when viewing data modal opens

  // eslint-disable-next-line react-hooks/exhaustive-deps

  React.useEffect(() => {

    const fetchLatestAppointmentData = async () => {

      if (showViewDataModal && viewing?.id) {

        try {

          const apptDoc = await getDoc(doc(db, "appointments", viewing.id));

          if (apptDoc.exists()) {

            const data = apptDoc.data();

            setViewing(prev => ({

              ...prev,

              attachments: data.attachments || [],

              attachmentNotes: data.attachmentNotes || []

            }));

          }

        } catch (err) {

          console.error("Error fetching latest appointment data:", err);

        }

      }

    };

    fetchLatestAppointmentData();

  }, [showViewDataModal, viewing?.id]);



  // Fetch latest appointment data when attachments modal opens

  // eslint-disable-next-line react-hooks/exhaustive-deps

  React.useEffect(() => {

    const fetchLatestAppointmentData = async () => {

      if (showAttachmentsModal && viewing?.id) {

        try {

          const apptDoc = await getDoc(doc(db, "appointments", viewing.id));

          if (apptDoc.exists()) {

            const data = apptDoc.data();

            setExistingNotes(data.attachmentNotes || []);

            setViewing(prev => ({

              ...prev,

              attachments: data.attachments || [],

              attachmentNotes: data.attachmentNotes || []

            }));

          }

        } catch (err) {

          console.error("Error fetching latest appointment data:", err);

        }

      }

    };

    fetchLatestAppointmentData();

  }, [showAttachmentsModal, viewing?.id]);



  // Helper to check if appointment is editable (within 7 days of completion/update)
  const isAppointmentEditable = (appt) => {
    if (!appt) return false;

    // Check blockchain status first - if read_only or verified, immediately not editable
    if (appt.blockchain?.status === 'read_only' || appt.blockchain?.status === 'verified') {
      return false;
    }

    // Use completedAt, or updatedAt, or createdAt in that order of preference
    const referenceDate = appt.completedAt?.toDate ? appt.completedAt.toDate() :
                         appt.updatedAt?.toDate ? appt.updatedAt.toDate() :
                         new Date(appt.date); // Fallback to appointment date

    const diffTime = new Date() - referenceDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    return diffDays <= 7;
  };

  // Helper to check if current admin is the assigned professional for the appointment
  const isAdminAssignedPro = (appt) => {
    if (!appt) return false;
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    // Check if current user is the assigned pro (admin-credited appointments)
    const assignedProId = appt.proId || appt.auditCompletedId;
    return assignedProId === currentUser.uid;
  };



  const handleAttachmentUpload = async () => {
    if (!clinicalNote.trim() && attachmentFiles.length === 0 && removedAttachmentIds.length === 0) {
      setNoteError("Please enter a note or select at least one file.");
      return;
    }

    if (clinicalNote.trim() && clinicalNote.trim().length < 10) {
      setNoteError("Notes must be at least 10 characters long.");
      return;
    }

    if (clinicalNote.length > 500) {
      setNoteError("Notes must not exceed 500 characters.");
      return;
    }

    // Security check: ensure admin is still the assigned professional
    if (!isAdminAssignedPro(viewing)) {
      setBannerMessage("You are not authorized to edit this appointment.");
      setBannerType("error");
      return;
    }

    setIsUploading(true);
    setNoteError("");

    try {
      const sanitizedNote = clinicalNote.trim().replace(/<[^>]*>?/gm, '');
      const uploadedBy = effectiveStaffProfile?.fullName || effectiveStaffProfile?.name || "Medical Professional";

      if (selectedNoteId) {
        // EDIT PATH: update existing note + manage file removals/additions
        await appointmentService.updateAttachmentContent(
          viewing.id,
          attachmentFiles,
          sanitizedNote,
          uploadedBy,
          viewing.attachments || [],
          removedAttachmentIds,
          selectedNoteId
        );
      } else {
        // NEW PATH: append fresh note + files
        await appointmentService.uploadAttachment(viewing.id, attachmentFiles, sanitizedNote, uploadedBy);
      }

      setNotificationMessage("Attachments and findings saved successfully!");
      setNotificationType("success");

      setClinicalNote("");
      setAttachmentFiles([]);
      setRemovedAttachmentIds([]);
      setSelectedNoteId("");
      setShowAttachmentEditor(false);

      // Invalidate cache and re-fetch
      delete appointmentDataCache.current[viewing.id];
      lastFetchedAppointmentId.current = null;

      const apptDoc = await getDoc(doc(db, "appointments", viewing.id));
      if (apptDoc.exists()) {
        const data = apptDoc.data();
        appointmentDataCache.current[viewing.id] = data;
        lastFetchedAppointmentId.current = viewing.id;
        setExistingNotes(data.attachmentNotes || []);
        setViewing(prev => ({
          ...prev,
          attachments: data.attachments || [],
          attachmentNotes: data.attachmentNotes || []
        }));
      }

      // Create notification for admin about the record update
      try {
        const patientDoc = await getDoc(doc(db, 'users', viewing.uid));
        const patientData = patientDoc.exists() ? patientDoc.data() : {};
        const patientUIC = patientData.uniqueIdentifierCode || patientData.accountSetup?.uniqueIdentifierCode || 'N/A';
        const patientName = patientData.fullName || 'Patient';

        await notificationService.createRecordUpdateNotification(
          auth.currentUser?.uid,
          viewing.reference,
          viewing.serviceName,
          viewing.id,
          patientUIC,
          patientName,
          auth.currentUser?.uid
        );
      } catch (notifErr) {
        console.error("Error creating admin notification for record update:", notifErr);
      }
    } catch (err) {
      console.error("Error uploading attachment:", err);
      setBannerMessage("Failed to upload. " + err.message);
      setBannerType("error");
    } finally {
      setIsUploading(false);
    }
  };



  // Staff ID validation handler - local comparison without Firestore reads

  const handleStaffIdChange = (e) => {

    const value = e.target.value;

    setStaffId(value);

    

    if (!value) {

      setStaffIdValid(false);

      setStaffIdError("");

      return;

    }

    

    // Accept legacy staff IDs, current staff IDs, and the fixed admin ID.

    if (value.length > 11) {

      setStaffIdValid(false);

      setStaffIdError("Invalid Staff ID format");

      return;

    }

    

    const legacyPattern = /^STAFF-\d{4}$/;

    const currentPattern = /^AURA\d{7}$/;

    if (value !== ADMIN_ACCOUNT_ID && !legacyPattern.test(value) && !currentPattern.test(value)) {

      setStaffIdValid(false);

      setStaffIdError("Staff ID must be in format AURA#######");

      return;

    }

    

    if (value === actualStaffId || (value === ADMIN_ACCOUNT_ID && actualStaffId === ADMIN_ACCOUNT_ID)) {

      setStaffIdValid(true);

      setStaffIdError("");

    } else {

      setStaffIdValid(false);

      setStaffIdError("Staff ID does not match your account");

    }

  };



  // Password input handlers - matching Login.jsx pattern

  const handlePasswordKeyDown = (e) => {

    if (e.key === " ") e.preventDefault();

  };



  const handlePasswordPaste = (e) => {

    e.preventDefault();

  };



  const handlePasswordChange = (e) => {

    const value = e.target.value;

    setStaffPassword(value);

    

    if (!value) {

      setPasswordError("");

      return;

    }

    

    // Validate length: 8-24 characters

    if (value.length < 8) {

      setPasswordError("Password must be at least 8 characters");

      return;

    }

    

    if (value.length > 24) {

      setPasswordError("Password must not exceed 24 characters");

      return;

    }

    

    // Check for repeated characters (3 or more consecutive same characters)

    const hasRepeatedChars = /(.)\1{2,}/.test(value);

    if (hasRepeatedChars) {

      setPasswordError("Password must not contain repeated characters");

      return;

    }

    

    setPasswordError("");

  };



  // Update current time every minute for expiry checks

  React.useEffect(() => {

    const timer = setInterval(() => {

      setCurrentTime(new Date());

    }, 60000);

    return () => clearInterval(timer);

  }, []);



  // Auto-close View Data Modal if access expires or is terminated

  React.useEffect(() => {

    if (showViewDataModal && viewing) {

      // Find the current user's access request
      const myRequest = getOwnAccessRequest(viewing);

      if (myRequest && !isAccessActive(myRequest, currentTime)) {

        setShowViewDataModal(false);

        setBannerMessage("Your data access has expired or was terminated.");

        setBannerType("error");

        

        // Clear banner after 5 seconds if modal closed automatically

        const timer = setTimeout(() => {

          setBannerMessage("");

        }, 5000);

        return () => clearTimeout(timer);

      }

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [currentTime, showViewDataModal, viewing?.dataAccessRequests]);



  // Subscribe to patient records in real-time when viewing data access is active

  // Memoization: Track last subscribed UID to avoid re-subscribing to same patient

  const lastSubscribedUid = React.useRef(null);

  const patientDataCache = React.useRef({});



  // eslint-disable-next-line react-hooks/exhaustive-deps

  React.useEffect(() => {

    let unsubscribe = () => {};



    const fetchPatientData = async () => {

      if (showViewDataModal && viewing?.uid) {

        // SECURITY CHECK: Only fetch if access is ACTIVE

        if (!isAccessActive(getOwnAccessRequest(viewing), currentTime)) {

          setPatientInfo(null);

          return;

        }



        setIsLoadingRecords(true);

        

        try {

          // Check cache for user document to avoid repeated fetches

          let userData;

          if (patientDataCache.current[viewing.uid]?.userDoc) {

            userData = patientDataCache.current[viewing.uid].userDoc;

          } else {

            // Fetch user document for medical history

            const userRef = doc(db, "users", viewing.uid);

            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {

              userData = userSnap.data();

              // Cache the user document

              if (!patientDataCache.current[viewing.uid]) {

                patientDataCache.current[viewing.uid] = {};

              }

              patientDataCache.current[viewing.uid].userDoc = userData;

            }

          }



          if (userData) {

            const isAssistedAccount = `${userData.assistanceRequired || userData.assistance?.assistanceRequired || ""}`.toLowerCase() === "yes";

            const guardianDetails = userData.guardianContactDetails || null;

            const hasGuardianDetails = Boolean(

              guardianDetails && (

                guardianDetails.guardianFullName ||

                guardianDetails.guardianRelationshipToUser ||

                guardianDetails.guardianContactNumber

              )

            );

            const locationParts = [

              userData.contactDetails?.barangay,

              userData.contactDetails?.province,

              userData.contactDetails?.region

            ].filter(Boolean);

            const formattedLocation = locationParts.length > 0

              ? locationParts.join(", ")

              : userData.personalInformation?.province || "";

            setPatientInfo({

              healthInformation: {

                medicalHistory: userData.medicalHistory || [],

                medicalOther: userData.medicalOther || "",

                pastSurgeries: userData.pastSurgeries || [],

                surgeryOther: userData.surgeryOther || "",

                allergies: userData.allergies || [],

                allergyOther: userData.allergyOther || "",

                smokingStatus: userData.smoking || "",

                alcoholUse: userData.alcoholUse || ""

              },

              medicationHistory: {

                currentMedications: userData.currentMedications || "",

                hormonalMedications: userData.hormonalMedications || "",

                otherContraception: userData.contraceptionOther || ""

              },

              sexualHistory: {

                stiHistory: userData.stiHistory || [],

                stiOther: userData.stiOther || "",

                hivStatus: userData.hivStatus || "",

                hivDetails: userData.hivOther || "",

                lastHivTest: userData.lastTestedDate || ""

              },

              patientInformation: {

                fullName: ((userData.personalInformation?.firstName || "") + " " + (userData.personalInformation?.lastName || "")).trim(),

                dateOfBirth: userData.personalInformation?.dob || "",

                sex: userData.personalInformation?.sex || "",

                location: formattedLocation,

                email: userData.email || ""

              },

              ...(isAssistedAccount && hasGuardianDetails && {

                guardianInformation: {

                  guardianFullName: guardianDetails.guardianFullName || "",

                  relationship: guardianDetails.guardianRelationshipToUser || "",

                  contactNumber: guardianDetails.guardianContactNumber || ""

                }

              })

            });

          }



          // Only subscribe if we're viewing a different patient than before

          if (lastSubscribedUid.current !== viewing.uid) {

            // Clean up previous subscription

            if (unsubscribe && typeof unsubscribe === 'function') {

              unsubscribe();

            }

            

            // Subscribe to appointments

            unsubscribe = appointmentService.subscribeToUserAppointments(viewing.uid, (allAppts) => {

              try {

                const recordsWithNotes = allAppts

                  .filter(a => a.status === 'COMPLETED' || a.id === viewing.id)

                  .map(a => ({

                    ...a,

                    attachments: a.attachments || [],

                    category: a.serviceId === 'sti' ? 'STI / Sexual Health Tests' :

                              a.serviceId === 'family' ? 'Screening / Preventive Checks' : 'Lab Tests',

                    displayNotes: (() => {

                      const storedNotes = [

                        ...(a.notes || []),

                        ...(a.attachmentNotes || [])

                      ];
                      const rawNotes = storedNotes.length > 0 ? storedNotes : (a.clinicalNotes || []);

                      const normalized = rawNotes.map(n => ({

                        id: n.noteId || n.id,

                        text: n.content || n.text,

                        shared: n.visibility === "shared" || n.shared === true,

                        createdAt: n.createdAt,

                        proName: n.proName || a.proName

                      }));

                      // Show ALL notes, no filtering by shared status
                      return normalized;

                    })()

                  }))

                  .filter(a => a.displayNotes.length > 0 || a.attachments?.length > 0 || a.id === viewing.id);

                
                setAllPatientAppointments(recordsWithNotes);

              } catch (err) {

                console.error("Error processing patient records:", err);

              } finally {

                setIsLoadingRecords(false);

              }

            });



            lastSubscribedUid.current = viewing.uid;

          } else {

            // Already subscribed to this patient, just update loading state

            setIsLoadingRecords(false);

          }

        } catch (err) {

          console.error("Error fetching patient data:", err);

          setIsLoadingRecords(false);

        }

      } else {

        setPatientInfo(null);

        lastSubscribedUid.current = null;

      }

    };



    fetchPatientData();



    return () => {

      if (unsubscribe && typeof unsubscribe === 'function') {

        unsubscribe();

      }

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [showViewDataModal, viewing?.uid, viewing?.id, viewing?.dataAccessRequest?.status, viewing?.dataAccessRequest?.expiresAt, currentTime]);

  // Load pending data access requests once for this staff member's appointments

  React.useEffect(() => {

    const loadPendingRequests = async () => {

      try {

        setRequestsLoading(true);

        if (!effectiveAppointments || effectiveAppointments.length === 0) {

          setPendingRequests({});

          setRequestsLoading(false);

          return;

        }

        // Build a lookup map of appointmentId -> requestId for pending requests
        const requestMap = {};

        const currentUser = auth.currentUser;

        

        for (const appointment of effectiveAppointments) {

          // Check if appointment has pending data access requests from this admin
          const dataAccessRequests = appointment.dataAccessRequests || {};
          const myPendingRequest = Object.values(dataAccessRequests).find(
            req => (req.professionalId === currentUser?.uid || req.proId === currentUser?.uid) &&
              (!req.accessScope || req.accessScope === accessScope) && req.status === 'PENDING'
          );

          if (myPendingRequest) {
            requestMap[appointment.id] = myPendingRequest.requestId;
          } else {
            requestMap[appointment.id] = null;
          }

        }

        

        setPendingRequests(requestMap);

      } catch (error) {

        console.error('Error loading pending requests:', error);

        setPendingRequests({});

      } finally {

        setRequestsLoading(false);

      }

    };

    loadPendingRequests();

  }, [effectiveAppointments, appointments, currentTime]);

  // Group appointments by patient and fetch patient data
  React.useEffect(() => {
    const fetchGroupedPatients = async () => {
      try {
        setLoadingPatients(true);
        
        if (!effectiveAppointments || effectiveAppointments.length === 0) {
          setGroupedPatients([]);
          setLoadingPatients(false);
          return;
        }

        // Group appointments by patient uid
        const patientMap = {};
        effectiveAppointments.forEach(apt => {
          if (apt.uid) {
            if (!patientMap[apt.uid]) {
              patientMap[apt.uid] = {
                uid: apt.uid,
                patientName: apt.patientName,
                appointments: []
              };
            }
            patientMap[apt.uid].appointments.push(apt);
          }
        });

        // Fetch patient data for each unique patient
        const patientsWithData = await Promise.all(
          Object.values(patientMap).map(async (patient) => {
            try {
              const userRef = doc(db, "users", patient.uid);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const userData = userSnap.data();
                const locationParts = [
                  userData.contactDetails?.barangay,
                  userData.contactDetails?.province,
                  userData.contactDetails?.region
                ].filter(Boolean);
                const formattedLocation = locationParts.length > 0
                  ? locationParts.join(", ")
                  : userData.personalInformation?.province || "";

                // Assistance information
                const assistanceRequired = userData.assistance?.assistanceRequired || userData.assistanceRequired || userData.accountSetup?.assistanceRequired || "";
                const reasonForAssistance = userData.assistance?.reasonForAssistance || userData.reasonForAssistance || userData.accountSetup?.reasonForAssistance || [];
                const reasonsArray = Array.isArray(reasonForAssistance) ? reasonForAssistance : (reasonForAssistance ? [reasonForAssistance] : []);

                return {
                  ...patient,
                  uic: userData.uniqueIdentifierCode || userData.accountSetup?.uniqueIdentifierCode || "Not Provided",
                  location: formattedLocation,
                  dob: userData.personalInformation?.dob || "Not Provided",
                  assistanceRequired,
                  reasonForAssistance: reasonsArray
                };
              }
              return {
                ...patient,
                uic: "Not Provided",
                location: "Not Provided",
                dob: "Not Provided"
              };
            } catch (err) {
              console.error("Error fetching patient data:", err);
              return {
                ...patient,
                uic: "Not Provided",
                location: "Not Provided",
                dob: "Not Provided"
              };
            }
          })
        );

        setGroupedPatients(patientsWithData);
      } catch (error) {
        console.error('Error grouping patients:', error);
        setGroupedPatients([]);
      } finally {
        setLoadingPatients(false);
      }
    };

    fetchGroupedPatients();
  }, [effectiveAppointments]);

  const handleRequestAccess = async (e) => {

    e.preventDefault();

    if (!accessDuration || !staffId || !staffPassword) {

      setBannerMessage("Please fill in all fields.");

      setBannerType("error");

      return;

    }



    // STEP 1: Validate Staff ID locally (no Firestore read)

    if (!staffIdValid || staffId !== actualStaffId) {

      setBannerMessage("The provided Staff ID does not match your account.");

      setBannerType("error");

      return;

    }



    setIsSubmitting(true);

    setBannerMessage("");



    try {

      const user = auth.currentUser;

      if (!user) throw new Error("No authenticated user");



      // STEP 2: Validate Password matching backend

      try {

        const credential = EmailAuthProvider.credential(user.email, staffPassword);

        await reauthenticateWithCredential(user, credential);

      } catch (pwErr) {

        console.error("Password validation failed:", pwErr);

        setBannerMessage("Incorrect password. Please try again.");

        setBannerType("error");

        setIsSubmitting(false);

        return;

      }



      // STEP 3: Fetch professional's information and save request

      let proName = 'Healthcare Professional';

      let proRole = 'Healthcare Professional';

      let proDepartment = 'Medical Services';

      try {

        const proUserRef = doc(db, 'users', user.uid);

        const proUserSnap = await getDoc(proUserRef);

        if (proUserSnap.exists()) {

          const proUserData = proUserSnap.data();

          const firstName = proUserData.personalInformation?.firstName || '';

          const lastName = proUserData.personalInformation?.lastName || '';

          proName = (firstName + ' ' + lastName).trim() || 'Healthcare Professional';

          proRole = proUserData.specialization || proUserData.role || 'Healthcare Professional';

            proDepartment = proUserData.department || 'Medical Services';

        }

      } catch (userErr) {

        console.warn("Could not fetch professional info, using defaults:", userErr);

      }

      const accessRequest = await appointmentService.requestDataAccess(

        viewing.id,

        user.uid,

        parseInt(accessDuration),

        actualStaffId,
        {
          accessScope,
          fullName: proName,
          role: proRole,
          department: proDepartment,
          staffId: actualStaffId
        }
      );

      // Update the viewing appointment to reflect the new pending request status

      // This ensures the button immediately disables without waiting for parent component refresh

      setViewing(prev => ({

        ...prev,

        dataAccessRequest: {

          status: 'PENDING',

          requestId: accessRequest.requestId,
          accessScope,

          professionalId: user.uid,

          proName: proName,

          proRole: proRole,

          proDepartment: proDepartment,

          durationWeeks: parseInt(accessDuration),

          requestedAt: new Date(),

          createdAt: new Date()

        }

      }));

      // Also update the pendingRequests lookup map for immediate UI feedback

      setPendingRequests(prev => ({

        ...prev,

        [viewing.id]: true

      }));

      setNotification({ message: "Access request sent successfully!", type: "success" });

      setTimeout(() => {

        setShowAccessModal(false);

        setNotification({ message: "", type: "" });

        setAccessDuration(1);

        setStaffId("");

        setStaffPassword("");

        setStaffIdValid(false);

        setStaffIdError("");

        // Note: pendingRequests state is NOT cleared here - it will be cleared when the appointment data is updated through real-time listener

      }, 2000);

    } catch (err) {

      console.error("Error requesting access:", err);

      setNotification({ message: "Failed to send access request: " + err.message, type: "error" });

    } finally {

      setIsSubmitting(false);

    }

  };



  const handleTerminateAccess = async () => {

    if (!viewing) return;

    setIsSubmitting(true);

    try {

      const request = getOwnAccessRequest(viewing);
      if (!request?.requestId) throw new Error("Your access request could not be found.");
      await appointmentService.terminateDataAccess(viewing.id, request.requestId);

      setBannerMessage("Access terminated successfully.");

      setBannerType("success");

      setTimeout(() => {

        setShowViewDataModal(false);

        setViewing(null);

        setBannerMessage("");

      }, 1500);

    } catch (err) {

      console.error("Error terminating access:", err);

      setBannerMessage("Failed to terminate access.");

      setBannerType("error");

    } finally {

      setIsSubmitting(false);

    }

  };



  const handleDownload = async (appt) => {
    createAppointmentSlipPDF(appt, {
      status: appt.status,
      proName: (appt.status === "COMPLETED" || appt.status === "ACCEPTED")
        ? (appt.proName || "Medical Professional")
        : "To Be Assigned",
    });
  };

  const exportToPDF = async () => {
    const pdf = createPDF({ title: "Records Management Report" });
    let startY = pdf.contentStartY;

    const exportPatients = groupedPatients.filter((patient) => {
      if (!patient?.appointments?.length) return false;

      return patient.appointments.some((apt) => {
        const searchLower = search.toLowerCase();
        const matchesSearch = !search || (
          (apt.patientName?.toLowerCase().includes(searchLower)) ||
          (apt.clinicName?.toLowerCase().includes(searchLower)) ||
          (apt.serviceName?.toLowerCase().includes(searchLower)) ||
          (apt.reference?.toLowerCase().includes(searchLower)) ||
          (getStatusLabel(apt.status).toLowerCase().includes(searchLower))
        );

        let matchesDate = true;
        if (dateFrom || dateTo) {
          const aptDate = new Date(apt.date);
          if (dateFrom) {
            const fromDate = new Date(dateFrom);
            matchesDate = matchesDate && aptDate >= fromDate;
          }
          if (dateTo) {
            const toDate = new Date(dateTo);
            matchesDate = matchesDate && aptDate <= toDate;
          }
        }

        const matchesStatus = !statusFilter || apt.status === statusFilter;
        const matchesService = !serviceFilter || apt.serviceName === serviceFilter;

        return matchesSearch && matchesDate && matchesStatus && matchesService;
      });
    });

    pdf.doc.setFontSize(10);
    pdf.doc.text(`Total Patients: ${exportPatients.length}`, pdf.margin, startY);

    if (serviceFilter) {
      startY += 10;
      pdf.doc.text(`Service: ${serviceFilter}`, pdf.margin, startY);
    }

    startY += 15;

    if (exportPatients.length === 0) {
      pdf.doc.setFontSize(12);
      pdf.doc.setTextColor(180, 0, 0);
      pdf.doc.text(serviceFilter ? `No patients found for ${serviceFilter}.` : 'No patients found.', pdf.margin, startY);
      pdf.doc.setTextColor(0, 0, 0);
    } else {
      const headers = ['Patient', 'UIC', 'Address', 'DOB'];
      const rows = exportPatients.map((patient) => [
        patient.patientName || 'N/A',
        patient.uic || 'N/A',
        patient.location || 'N/A',
        patient.dob || 'N/A'
      ]);

      pdf.autoTable({
        head: [headers],
        body: rows,
        startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin },
        styles: { overflow: 'linebreak' }
      });
    }

    pdf.save(`admin-records-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();
    const exportPatients = groupedPatients.filter((patient) => {
      if (!patient?.appointments?.length) return false;
      return patient.appointments.some((apt) => {
        const searchLower = search.toLowerCase();
        const matchesSearch = !search || (
          (apt.patientName?.toLowerCase().includes(searchLower)) ||
          (apt.clinicName?.toLowerCase().includes(searchLower)) ||
          (apt.serviceName?.toLowerCase().includes(searchLower)) ||
          (apt.reference?.toLowerCase().includes(searchLower)) ||
          (getStatusLabel(apt.status).toLowerCase().includes(searchLower))
        );

        let matchesDate = true;
        if (dateFrom || dateTo) {
          const aptDate = new Date(apt.date);
          if (dateFrom) {
            const fromDate = new Date(dateFrom);
            matchesDate = matchesDate && aptDate >= fromDate;
          }
          if (dateTo) {
            const toDate = new Date(dateTo);
            matchesDate = matchesDate && aptDate <= toDate;
          }
        }

        const matchesStatus = !statusFilter || apt.status === statusFilter;
        const matchesService = !serviceFilter || apt.serviceName === serviceFilter;

        return matchesSearch && matchesDate && matchesStatus && matchesService;
      });
    });

    const summaryData = [
      ['RECORDS MANAGEMENT REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      ...(serviceFilter ? [['Service', serviceFilter]] : []),
      [],
      ['RECORDS SUMMARY'],
      ['Total Patients', exportPatients.length],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    const contentData = [
      ['RECORD DETAILS'],
      ...(serviceFilter ? [['Service', serviceFilter]] : []),
      ...(exportPatients.length === 0
        ? [[serviceFilter ? `No patients found for ${serviceFilter}.` : 'No patients found.']]
        : [
            ['Patient', 'UIC', 'Address', 'DOB'],
            ...exportPatients.map((patient) => [
              patient.patientName || 'N/A',
              patient.uic || 'N/A',
              patient.location || 'N/A',
              patient.dob || 'N/A'
            ])
          ])
    ];

    const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
    XLSX.utils.book_append_sheet(workbook, contentSheet, 'Records');
    XLSX.writeFile(workbook, `admin-records-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async () => {
    const exportPatients = groupedPatients.filter((patient) => {
      if (!patient?.appointments?.length) return false;
      return patient.appointments.some((apt) => {
        const searchLower = search.toLowerCase();
        const matchesSearch = !search || (
          (apt.patientName?.toLowerCase().includes(searchLower)) ||
          (apt.clinicName?.toLowerCase().includes(searchLower)) ||
          (apt.serviceName?.toLowerCase().includes(searchLower)) ||
          (apt.reference?.toLowerCase().includes(searchLower)) ||
          (getStatusLabel(apt.status).toLowerCase().includes(searchLower))
        );

        let matchesDate = true;
        if (dateFrom || dateTo) {
          const aptDate = new Date(apt.date);
          if (dateFrom) {
            const fromDate = new Date(dateFrom);
            matchesDate = matchesDate && aptDate >= fromDate;
          }
          if (dateTo) {
            const toDate = new Date(dateTo);
            matchesDate = matchesDate && aptDate <= toDate;
          }
        }

        const matchesStatus = !statusFilter || apt.status === statusFilter;
        const matchesService = !serviceFilter || apt.serviceName === serviceFilter;

        return matchesSearch && matchesDate && matchesStatus && matchesService;
      });
    });

    let csvData = [
      ['RECORDS MANAGEMENT REPORT'],
      ['Generated', new Date().toLocaleString()],
      ['Status Filter', statusFilter || 'All Statuses'],
      ...(serviceFilter ? [['Service', serviceFilter]] : []),
      [],
      ['RECORDS SUMMARY'],
      ['Total Patients', exportPatients.length],
      [],
      ['RECORD DETAILS'],
      ...(serviceFilter ? [['Service', serviceFilter]] : []),
      ...(exportPatients.length === 0
        ? [[serviceFilter ? `No patients found for ${serviceFilter}.` : 'No patients found.']]
        : [
            ['Patient', 'UIC', 'Address', 'DOB'],
            ...exportPatients.map((patient) => [
              patient.patientName || 'N/A',
              patient.uic || 'N/A',
              patient.location || 'N/A',
              patient.dob || 'N/A'
            ])
          ])
    ];

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');

    link.href = URL.createObjectURL(blob);

    link.download = `admin-records-report-${new Date().toISOString().split('T')[0]}.csv`;

    link.click();

  };

  const handleExport = async (format) => {

    try {

      setIsExporting(true);

      setSelectedExportFormat(format);

      

      if (format === 'pdf') {

        await exportToPDF();

      } else if (format === 'excel') {

        await exportToExcel();

      } else if (format === 'csv') {

        await exportToCSV();

      }

    } catch (error) {

      console.error('Export error:', error);

      setBannerMessage('Export failed');

      setBannerType('error');

    } finally {

      setIsExporting(false);

      setSelectedExportFormat(null);

      setIsExportModalOpen(false);

    }

  };



  const filteredAppointments = effectiveAppointments.filter((apt) => {

    const searchLower = search.toLowerCase();

    

    const matchesSearch = !search || (

      (apt.patientName?.toLowerCase().includes(searchLower)) ||

      (apt.clinicName?.toLowerCase().includes(searchLower)) ||

      (apt.serviceName?.toLowerCase().includes(searchLower)) ||

      (apt.reference?.toLowerCase().includes(searchLower)) ||

      (getStatusLabel(apt.status).toLowerCase().includes(searchLower))

    );



    let matchesDate = true;

    if (dateFrom || dateTo) {

      const aptDate = new Date(apt.date);

      if (dateFrom) {

        const fromDate = new Date(dateFrom);

        matchesDate = matchesDate && aptDate >= fromDate;

      }

      if (dateTo) {

        const toDate = new Date(dateTo);

        matchesDate = matchesDate && aptDate <= toDate;

      }

    }

    

    const matchesService = !serviceFilter || apt.serviceName === serviceFilter;

    const matchesStatus = !statusFilter || apt.status === statusFilter;



    return matchesSearch && matchesDate && matchesService && matchesStatus;

  });



  const getStatusTabCount = (tabValue) => {

    const searchLower = search.toLowerCase();

    

    return effectiveAppointments.filter(apt => {

      const matchesSearch = !search || (

        (apt.patientName?.toLowerCase().includes(searchLower)) ||

        (apt.clinicName?.toLowerCase().includes(searchLower)) ||

        (apt.serviceName?.toLowerCase().includes(searchLower)) ||

        (apt.reference?.toLowerCase().includes(searchLower)) ||

        (getStatusLabel(apt.status).toLowerCase().includes(searchLower))

      );



      let matchesDate = true;

      if (dateFrom || dateTo) {

        const aptDate = new Date(apt.date);

        if (dateFrom) {

          const fromDate = new Date(dateFrom);

          matchesDate = matchesDate && aptDate >= fromDate;

        }

        if (dateTo) {

          const toDate = new Date(dateTo);

          matchesDate = matchesDate && aptDate <= toDate;

        }

      }

      

      const matchesService = !serviceFilter || apt.serviceName === serviceFilter;

      const matchesStatus = !tabValue || apt.status === tabValue;



      return matchesSearch && matchesDate && matchesService && matchesStatus;

    }).length;

  };

  const getServiceTabCount = (tabValue) => {
    return groupedPatients.filter((patient) => {
      if (!patient?.appointments?.length) return false;

      const searchLower = search.toLowerCase();

      return patient.appointments.some((apt) => {
        const matchesSearch = !search || (
          (apt.patientName?.toLowerCase().includes(searchLower)) ||
          (apt.clinicName?.toLowerCase().includes(searchLower)) ||
          (apt.serviceName?.toLowerCase().includes(searchLower)) ||
          (apt.reference?.toLowerCase().includes(searchLower)) ||
          (getStatusLabel(apt.status).toLowerCase().includes(searchLower))
        );

        let matchesDate = true;
        if (dateFrom || dateTo) {
          const aptDate = new Date(apt.date);
          if (dateFrom) {
            const fromDate = new Date(dateFrom);
            matchesDate = matchesDate && aptDate >= fromDate;
          }
          if (dateTo) {
            const toDate = new Date(dateTo);
            matchesDate = matchesDate && aptDate <= toDate;
          }
        }

        const matchesStatus = !statusFilter || apt.status === statusFilter;
        const matchesService = !tabValue || apt.serviceName === tabValue;

        return matchesSearch && matchesDate && matchesStatus && matchesService;
      });
    }).length;
  };



  // Pagination logic
  // Set loading to false when appointments are loaded (even if empty)
  useEffect(() => {
    setLoading(false);
  }, [effectiveAppointments]);

  const getPatientSortValue = (patient, key) => {
    switch (key) {
      case 'patientName':
        return patient.patientName || '';
      case 'uic':
        return patient.uic || '';
      case 'location':
        return patient.location || '';
      case 'dob':
        return patient.dob ? new Date(patient.dob) : null;
      default:
        return patient.patientName || '';
    }
  };

  const getNextSortConfig = (current, key) => {
    if (current?.key !== key) return { key, direction: 'asc' };
    if (current.direction === 'asc') return { key, direction: 'desc' };
    return null;
  };

  const handleSort = (key) => {
    setSortConfig((current) => getNextSortConfig(current, key));
    setCurrentPage(1);
  };

  const handleSortHeaderProps = (key, label) => ({
    onClick: () => handleSort(key),
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSort(key);
      }
    },
    tabIndex: 0,
    style: { cursor: 'pointer' },
    title: sortConfig?.key === key
      ? `Sorted by ${label} ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}`
      : `Sort by ${label}`,
    'aria-label': sortConfig?.key === key
      ? `Sorted by ${label} ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}`
      : `Sort by ${label}`,
    'aria-sort': sortConfig?.key !== key ? 'none' : (sortConfig.direction === 'asc' ? 'ascending' : 'descending')
  });

  const patientsByService = groupedPatients
    .filter((patient) => {
      if (!serviceFilter) return true;
      return patient.appointments?.some((apt) => apt.serviceName === serviceFilter) || false;
    })
    .sort((a, b) => {
      const aLatestCompleted = a.appointments
        ?.filter((apt) => apt.status === 'COMPLETED')
        .reduce((latest, apt) => {
          const aptDate = new Date(apt.date || 0).getTime();
          return aptDate > latest ? aptDate : latest;
        }, 0) || 0;

      const bLatestCompleted = b.appointments
        ?.filter((apt) => apt.status === 'COMPLETED')
        .reduce((latest, apt) => {
          const aptDate = new Date(apt.date || 0).getTime();
          return aptDate > latest ? aptDate : latest;
        }, 0) || 0;

      return bLatestCompleted - aLatestCompleted;
    });

  const filteredPatientsByService = sortConfig
    ? [...patientsByService].sort((a, b) => {
        const aValue = getPatientSortValue(a, sortConfig.key);
        const bValue = getPatientSortValue(b, sortConfig.key);

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (aValue instanceof Date && bValue instanceof Date) {
          const comparison = aValue.getTime() - bValue.getTime();
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        const aText = String(aValue ?? '');
        const bText = String(bValue ?? '');
        return sortConfig.direction === 'asc'
          ? aText.localeCompare(bText, undefined, { numeric: true, sensitivity: 'base' })
          : bText.localeCompare(aText, undefined, { numeric: true, sensitivity: 'base' });
      })
    : patientsByService;

  const totalPages = Math.ceil(filteredPatientsByService.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;

  const endIndex = startIndex + itemsPerPage;

  const paginatedPatients = filteredPatientsByService.slice(startIndex, endIndex);



  const renderInfoSection = (title, data) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <div className="ma-patient-summary" style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', fontWeight: '700' }}>{title}</h4>
        <div className="ma-modal-details" style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          {Object.entries(data).map(([key, value]) => {
            if (!value || (Array.isArray(value) && value.length === 0)) return null;
            
            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            
            return (
              <div key={key} className="ma-detail-row" style={{ padding: '8px 0', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
                <span className="ma-detail-label" style={{ color: '#64748b', fontSize: '13px' }}>{label}</span>
                <span className="ma-detail-value" style={{ color: '#1e293b', fontWeight: '500', fontSize: '13px', display: 'flex', justifyContent: 'flex-end', textAlign: 'right' }}>
                  {Array.isArray(value) ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                      {value.map((item, idx) => (
                        <span key={idx} style={{ padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px', fontSize: '11px' }}>{item}</span>
                      ))}
                    </div>
                  ) : value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };



  return (

    <div className="account-management-container">

      <NotificationModal
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />

      <NotificationModal
        message={notificationMessage}
        type={notificationType}
        onClose={() => setNotificationMessage("")}
      />

      <ExportModal 

        isOpen={isExportModalOpen} 

        onClose={() => setIsExportModalOpen(false)} 

        onExport={handleExport} 

        isExporting={isExporting} 

      />

      {/* Admin Records Header */}

      <div className="content-library-header-custom" style={{

        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',

        borderRadius: '20px',

        padding: '32px',

        marginBottom: '24px',

        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',

        position: 'relative',

        overflow: 'hidden'

      }}>

        {/* Administrator - Left Aligned */}
        <div style={{
          position: 'absolute',
          left: '32px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div style={{
            padding: '16px 32px',
            borderRadius: '50px',
            background: 'rgba(147, 197, 253, 0.2)',
            border: '1px solid rgba(147, 197, 253, 0.3)'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.02em',
              textShadow: 'none'
            }}>
              Administrator
            </h2>
          </div>
        </div>

        {/* Title and Subtitle */}

        <div style={{ textAlign: 'right' }}>

          <h2 style={{

            margin: 0,

            fontSize: '36px',

            fontWeight: '800',

            color: '#ffffff !important',

            background: 'none !important',

            WebkitBackgroundClip: 'unset !important',

            WebkitTextFillColor: '#ffffff !important',

            backgroundClip: 'unset !important',

            letterSpacing: '-0.02em',

            textShadow: '0 2px 8px rgba(0,0,0,0.2)'

          }}>

            Records Management

          </h2>

          <p style={{

            margin: '12px 0 0 0',

            fontSize: '18px',

            color: '#ffffff',

            fontWeight: '500',

            textShadow: '0 1px 4px rgba(0,0,0,0.15)'

          }}>

            View and manage all patient medical records

          </p>

        </div>

        {/* CSS Animations */}

        <style>{`

          .content-library-header-custom h2 {

            color: #ffffff !important;

            background: none !important;

            -webkit-background-clip: unset !important;

            -webkit-text-fill-color: #ffffff !important;

            background-clip: unset !important;

          }

          .content-library-header-custom p {

            color: #ffffff !important;

          }

        `}</style>

      </div>



      {/* Top Row: Search, Date From, Date To, Service, Refresh */}

      <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>

        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>

          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />

          <input

            type="text"

            placeholder="Search Records"

            value={search}

            onChange={(e) => {

              setSearch(e.target.value);

              setCurrentPage(1);

            }}

            className="admin-aura-search"

            style={{ paddingLeft: 36, width: '100%' }}

          />

        </div>

        <button 

          onClick={async () => {

            setIsRefreshing(true);

            setCurrentPage(1);

            try {

              if (typeof refreshAppointments === 'function') {

                await refreshAppointments();

              }

            } catch (err) {

              console.error('Error refreshing records:', err);

            } finally {

              setIsRefreshing(false);

            }

          }}

          disabled={isRefreshing}

          className="admin-primary-gradient-btn"

          style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}

        >

          <FiRefreshCw className={isRefreshing ? "spinning" : ""} />

          {isRefreshing ? "Refreshing..." : "Refresh"}

        </button>

        <button

          onClick={() => setIsExportModalOpen(true)}

          className="admin-primary-gradient-btn"

        >

          <FiFileText /> Export Report

        </button>

      </div>



      {/* Custom Tooltip for Action Buttons */}

      {tooltip.visible && (

        <div

          className={`admin-action-tooltip ${tooltip.type}`}

          style={{

            left: tooltip.x,

            top: tooltip.y

          }}

        >

          {tooltip.text}

        </div>

      )}



      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>

        <div className="floating-index-tabs ma-status-tabs" aria-label="Filter records by service" style={{ display: "flex", gap: "8px" }}>

          {SERVICE_FILTER_TABS.map((tab) => (

            <button

              key={tab.value || "all"}

              type="button"

              className={`floating-tab ${serviceFilter === tab.value ? "active floating-tab-active" : ""}`}

              onClick={() => {
                setServiceFilter(tab.value);
                setCurrentPage(1);
              }}

            >

              {tab.label}

              <span className="tab-count">{getServiceTabCount(tab.value)}</span>

            </button>

          ))}

        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {filteredPatientsByService.length > 0 ? (
                `${startIndex + 1}-${Math.min(endIndex, filteredPatientsByService.length)} of ${filteredPatientsByService.length}`
              ) : (
                '0 of 0'
              )}
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  backgroundColor: '#1e3a8a',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                ←
              </button>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{
                  backgroundColor: '#1e3a8a',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '32px'
                }}
              >
                →
              </button>
            </div>
        </div>

      </div>

      <div className="ma-table-wrap">

        <table className="ma-table">

          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
              <th {...handleSortHeaderProps('patientName', 'Patient')} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>PATIENT {sortConfig?.key === 'patientName' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}</th>
              <th {...handleSortHeaderProps('uic', 'UIC')} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>UIC {sortConfig?.key === 'uic' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}</th>
              <th {...handleSortHeaderProps('location', 'Address')} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>ADDRESS {sortConfig?.key === 'location' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}</th>
              <th {...handleSortHeaderProps('dob', 'DOB')} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>DOB {sortConfig?.key === 'dob' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'transparent', border: 'none' }}>ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {loadingPatients ? (
              <>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="admin-aura-skeleton-row">
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '60%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 10}%` }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40%' }} /></td>
                    <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                  </tr>
                ))}
              </>
            ) : filteredPatientsByService.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  {serviceFilter ? `No patients found for ${serviceFilter}.` : 'No patients found.'}
                </td>
              </tr>
            ) : (
              paginatedPatients.map((patient) => {
                const getLatestCompletedTimestamp = (apt) => {
                  const candidate = apt.completedAt?.toDate ? apt.completedAt.toDate() :
                    apt.updatedAt?.toDate ? apt.updatedAt.toDate() :
                    new Date(apt.date || 0);
                  return candidate.getTime();
                };

                const mostRecentCompleted = patient.appointments
                  .filter(apt => apt.status === 'COMPLETED')
                  .sort((a, b) => getLatestCompletedTimestamp(b) - getLatestCompletedTimestamp(a))[0];

                // Check if current admin has active access
                const dataAccessRequests = mostRecentCompleted?.dataAccessRequests || {};
                const myActiveRequest = Object.values(dataAccessRequests).find(
                  req => (req.professionalId === auth.currentUser?.uid || req.proId === auth.currentUser?.uid) && 
                  (!req.accessScope || req.accessScope === accessScope) &&
                  req.status === 'ACTIVE' && 
                  isAccessActive(req, currentTime)
                );
                const hasOwnActiveAccess = !!myActiveRequest;

                return (
                  <tr
                    key={patient.uid}
                    className={patient.uid === highlightedPatientUid ? 'highlighted-row' : ''}
                  >
                    <td>
                      <div>{patient.patientName}</div>
                      {patient.reasonForAssistance?.length > 0 && (
                        <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {patient.reasonForAssistance.map((reason, idx) => (
                            <span
                              key={idx}
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '500',
                                textTransform: 'capitalize',
                                backgroundColor: getAssistanceBadgeColor(reason),
                                color: getAssistanceBadgeTextColor(reason)
                              }}
                            >
                              {reason}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{patient.uic}</td>
                    <td>{patient.location}</td>
                    <td>{patient.dob}</td>
                    <td>
                      <div className="ma-row-actions">
                        {mostRecentCompleted && (
                          <>
                            {!hasOwnActiveAccess && !pendingRequests[mostRecentCompleted.id] && (
                              <button
                                type="button"
                                className="ma-action-btn view"
                                onClick={() => {
                                  setViewing(mostRecentCompleted);
                                  setShowAccessModal(true);
                                }}
                                style={{ background: '#ecfdf5', color: '#059669', borderColor: '#10b981' }}
                                onMouseEnter={(e) => { e.target.style.background = '#d1fae5'; handleActionMouseEnter(e, "Request Data Access", 'approve'); }}
                                onMouseMove={handleActionMouseMove}
                                onMouseLeave={(e) => { e.target.style.background = '#ecfdf5'; handleActionMouseLeave(); }}
                              >
                                <FiUnlock size={16} />
                              </button>
                            )}

                            {pendingRequests[mostRecentCompleted.id] && !hasOwnActiveAccess && (
                              <button
                                type="button"
                                className="ma-action-btn view"
                                disabled
                                style={{ opacity: 0.7, cursor: 'not-allowed', background: '#f3f4f6', color: '#6b7280', borderColor: '#d1d5db' }}
                              >
                                <FiLock size={16} />
                              </button>
                            )}

                            {hasOwnActiveAccess && (
                              <button
                                type="button"
                                className="ma-action-btn view"
                                onClick={() => {
                                  setViewing({ ...mostRecentCompleted, dataAccessRequest: myActiveRequest });
                                  setShowViewDataModal(true);
                                }}
                                style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#3b82f6' }}
                                onMouseEnter={(e) => { e.target.style.background = '#bfdbfe'; handleActionMouseEnter(e, "View Data", 'view'); }}
                                onMouseMove={handleActionMouseMove}
                                onMouseLeave={(e) => { e.target.style.background = '#dbeafe'; handleActionMouseLeave(); }}
                              >
                                <FiEye size={16} />
                              </button>
                            )}

                            {mostRecentCompleted.status === 'COMPLETED' && (
                              <button
                                type="button"
                                className={`ma-action-btn notes ${!isAppointmentEditable(mostRecentCompleted) || !isAdminAssignedPro(mostRecentCompleted) ? 'pending' : ''}`}
                                disabled={!isAppointmentEditable(mostRecentCompleted) || !isAdminAssignedPro(mostRecentCompleted)}
                                onClick={() => {
                                  setViewing(mostRecentCompleted);
                                  setShowAttachmentsModal(true);
                                }}
                                style={
                                  !isAppointmentEditable(mostRecentCompleted) || !isAdminAssignedPro(mostRecentCompleted)
                                    ? { background: '#f9fafb', color: '#9ca3af', border: '1px solid #e5e7eb', cursor: 'not-allowed', opacity: 0.6 }
                                    : { background: '#fef3c7', color: '#d97706', borderColor: '#f59e0b' }
                                }
                                onMouseEnter={(e) => {
                                  if (isAppointmentEditable(mostRecentCompleted) && isAdminAssignedPro(mostRecentCompleted)) {
                                    e.target.style.background = '#fde68a';
                                    handleActionMouseEnter(e, "Edit Attachments", 'reschedule');
                                  }
                                }}
                                onMouseMove={handleActionMouseMove}
                                onMouseLeave={(e) => {
                                  if (isAppointmentEditable(mostRecentCompleted) && isAdminAssignedPro(mostRecentCompleted)) {
                                    e.target.style.background = '#fef3c7';
                                    handleActionMouseLeave();
                                  }
                                }}
                              >
                                <FiPaperclip size={16} />
                              </button>
                            )}

                            <button
                              type="button"
                              className="ma-action-btn view"
                              onClick={async () => {
                                setViewing(patient);
                                setShowRecordsModal(true);
                                setLoadingRecords(true);
                                try {
                                  // Fetch full appointment data with attachments and notes
                                  const appointmentsWithDetails = await Promise.all(
                                    patient.appointments.map(async (apt) => {
                                      const aptDoc = await getDoc(doc(db, "appointments", apt.id));
                                      if (aptDoc.exists()) {
                                        const appointmentData = aptDoc.data();
                                        const attachmentNotes = appointmentData.attachmentNotes || [];
                                        const legacyNotes = appointmentData.notes || appointmentData.clinicalNotes || [];
                                        return {
                                          ...apt,
                                          attachments: appointmentData.attachments || [],
                                          attachmentNotes: [
                                            ...attachmentNotes,
                                            ...legacyNotes.map(note => ({
                                              id: note.noteId || note.id,
                                              text: note.content || note.text,
                                              createdAt: note.createdAt,
                                              createdBy: note.createdBy,
                                              proName: note.proName || apt.proName
                                            }))
                                          ]
                                        };
                                      }
                                      return apt;
                                    })
                                  );
                                  setPatientRecords(appointmentsWithDetails);
                                } catch (err) {
                                  console.error("Error fetching patient records:", err);
                                } finally {
                                  setLoadingRecords(false);
                                }
                              }}
                              style={{ background: '#e0e7ff', color: '#4338ca', borderColor: '#6366f1' }}
                              onMouseEnter={(e) => { e.target.style.background = '#c7d2fe'; handleActionMouseEnter(e, "View Records", 'view'); }}
                              onMouseMove={handleActionMouseMove}
                              onMouseLeave={(e) => { e.target.style.background = '#e0e7ff'; handleActionMouseLeave(); }}
                            >
                              <FiFileText size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>

        </table>

      </div>

      {/* ACCESS REQUEST MODAL */}

      {showAccessModal && viewing && createPortal(

        <div className="ma-modal-backdrop">

          <div className="ma-modal-card" style={{ maxWidth: '450px' }}>

            <button

              className="ma-modal-close"

              type="button"

              onClick={() => {

                setShowAccessModal(false);

                setNotification({ message: "", type: "" });

              }}

            >

              <FiX />

            </button>

            <h3>Request Patient Data Access</h3>

            <div className="ma-modal-details" style={{ marginBottom: '24px' }}>

              <div className="ma-detail-row">

                <span className="ma-detail-label">Patient</span>

                <span className="ma-detail-value">{viewing?.patientName}</span>

              </div>

              <div className="ma-detail-row">

                <span className="ma-detail-label">Reference #</span>

                <span className="ma-detail-value">{viewing?.reference}</span>

              </div>

            </div>

            <form onSubmit={handleRequestAccess}>

              <div className="ma-form-field">

                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Duration of Access (Weeks)</label>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                  <button

                    type="button"

                    onClick={() => setAccessDuration(prev => Math.max(1, (parseInt(prev) || 1) - 1))}

                    style={{

                      width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #d1d5db',

                      background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',

                      fontSize: '20px', fontWeight: '600', color: '#374151'

                    }}

                  >

                    -

                  </button>

                  <div style={{

                    flex: 1, padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',

                    textAlign: 'center', fontSize: '15px', fontWeight: '600', color: '#111827', background: '#f9fafb'

                  }}>

                    {accessDuration} week{accessDuration !== 1 ? 's' : ''}

                  </div>

                  <button

                    type="button"

                    onClick={() => setAccessDuration(prev => Math.min(4, (parseInt(prev) || 1) + 1))}

                    style={{

                      width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #d1d5db',

                      background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',

                      fontSize: '20px', fontWeight: '600', color: '#374151'

                    }}

                  >

                    +

                  </button>

                </div>

                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>Minimum: 1 week, Maximum: 4 weeks</p>

              </div>

              <div className="ma-form-field" style={{ marginTop: '20px' }}>

                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Your Staff/Admin ID</label>

                <input

                  type="text"

                  placeholder="Enter Admin ID"

                  value={staffId}

                  onChange={handleStaffIdChange}

                  maxLength={11}

                  required

                  style={{

                    width: '100%',

                    padding: '12px',

                    borderRadius: '8px',

                    border: staffIdError ? '1px solid #ef4444' : '1px solid #e5e7eb',

                    fontSize: '14px',

                    color: '#374151',

                    outline: 'none',

                    transition: 'border-color 0.2s'

                  }}

                />

                {staffIdError && <p style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{staffIdError}</p>}

              </div>

              <div className="ma-form-field" style={{ marginTop: '20px' }}>

                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Account Password</label>

                <div style={{ position: 'relative' }}>

                  <input

                    type={showPassword ? "text" : "password"}

                    placeholder="Enter your password to confirm"

                    value={staffPassword}

                    onChange={handlePasswordChange}

                    onKeyDown={handlePasswordKeyDown}

                    onPaste={handlePasswordPaste}

                    maxLength={24}

                    required

                    style={{

                      width: '100%',

                      padding: '12px',

                      paddingRight: '40px',

                      borderRadius: '8px',

                      border: passwordError ? '1px solid #ef4444' : '1px solid #e5e7eb',

                      fontSize: '14px',

                      color: '#374151',

                      outline: 'none',

                      transition: 'border-color 0.2s'

                    }}

                  />

                  <button

                    type="button"

                    onClick={() => setShowPassword(!showPassword)}

                    style={{

                      position: 'absolute',

                      right: '12px',

                      top: '50%',

                      transform: 'translateY(-50%)',

                      background: 'none',

                      border: 'none',

                      cursor: 'pointer',

                      color: '#6b7280',

                      padding: '4px'

                    }}

                  >

                    {showPassword ? <FiEye size={16} /> : <FiEyeOff size={16} />}

                  </button>

                </div>

                {passwordError && <p style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{passwordError}</p>}

              </div>

              <div className="ma-modal-actions" style={{ marginTop: '24px' }}>

                <button

                  type="button"

                  className="ma-modal-btn secondary"

                  onClick={() => {

                    setShowAccessModal(false);

                    setBannerMessage("");

                  }}

                >

                  Cancel

                </button>

                <button

                  type="submit"

                  className="ma-modal-btn primary"

                  disabled={isSubmitting}

                >

                  {isSubmitting ? "Processing..." : "Request Access"}

                </button>

              </div>

            </form>

          </div>

        </div>,

        document.body

      )}

      {/* VIEW DATA MODAL */}

      {showViewDataModal && viewing && createPortal(

        <div className="ma-modal-backdrop">

          <div className="ma-modal-card" style={{ maxWidth: '600px' }}>

            <button

              className="ma-modal-close"

              type="button"

              onClick={() => {

                setShowViewDataModal(false);

              }}

            >

              <FiX />

            </button>

            <h3>Patient Records - {viewing.patientName}</h3>

            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>

              Viewing approved clinical notes and appointment details.

            </p>

            <div className="ma-view-data-content" style={{ maxHeight: '600px', overflowY: 'auto', marginBottom: '20px', paddingRight: '12px', paddingBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '12px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #dcfce7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontSize: '14px', fontWeight: '600' }}>
                  <FiShield size={18} />
                  <span>Secure Access Active</span>
                </div>
                {viewing?.dataAccessRequest?.expiresAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#166534', fontSize: '14px', fontWeight: '500' }}>
                    <FiClock size={16} />
                    <span>Expires: {formatAccessExpiry(viewing.dataAccessRequest.expiresAt)}</span>
                  </div>
                )}
              </div>

              {/* Patient Medical History Sections */}
              {patientInfo && (
                <>
                  {renderInfoSection("Health Information", patientInfo.healthInformation)}
                  {renderInfoSection("Medication History", patientInfo.medicationHistory)}
                  {renderInfoSection("Sexual Health History", patientInfo.sexualHistory)}
                  {renderInfoSection("Patient Information", patientInfo.patientInformation)}
                  {patientInfo.guardianInformation && renderInfoSection("Guardian / Accompanist Information", patientInfo.guardianInformation)}
                </>
              )}

              <div className="ma-patient-summary" style={{ marginBottom: '24px' }}>

                <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px', fontWeight: '700' }}>Current Appointment</h4>

                <div className="ma-modal-details" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>

                  <div className="ma-detail-row">

                    <span className="ma-detail-label">Service</span>

                    <span className="ma-detail-value" style={{ color: '#111827', fontWeight: '600' }}>{viewing.serviceName}</span>

                  </div>

                  <div className="ma-detail-row">

                    <span className="ma-detail-label">Date & Time</span>

                    <span className="ma-detail-value">{viewing.date} • {viewing.time}</span>

                  </div>

                  <div className="ma-detail-row">

                    <span className="ma-detail-label">Reference</span>

                    <span className="ma-detail-value" style={{ fontFamily: 'monospace', color: '#2563eb' }}>{viewing.reference}</span>

                  </div>

                </div>

              </div>

              {false && (<>
              <h4 style={{ fontSize: '14px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>

                <FiPaperclip size={18} color="#2563eb" />

                Attachments & Findings

                {isLoadingRecords && <div className="ma-loader-small" style={{ width: '12px', height: '12px', border: '2px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'ma-spin 1s linear infinite' }}></div>}

              </h4>

              <div className="ma-attachments-findings-section" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {allPatientAppointments && allPatientAppointments.length > 0 ? (
                  allPatientAppointments.map((apt) => {
                    const notes = apt.displayNotes || [];
                    const attachments = apt.attachments || [];

                    if (notes.length === 0 && attachments.length === 0) return null;

                    return (
                      <div key={apt.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                        <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                          {apt.serviceName} - {apt.date} • {apt.time}
                        </h5>

                        {notes.length > 0 && (
                          <div style={{ marginBottom: '16px' }}>
                            <h6 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Findings</h6>
                            {notes.map((note, idx) => (
                              <div key={idx} style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                                  <span style={{ fontWeight: '600' }}>By {note.proName || note.createdBy}</span> • {new Date(note.createdAt).toLocaleDateString()} • {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {attachments.length > 0 && (
                          <div>
                            <h6 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Uploads</h6>
                            {attachments.map((file, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                                  {file.fileType?.includes('image') ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                  ) : file.fileType?.includes('pdf') ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                  ) : (
                                    <FiPaperclip size={16} />
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '600', color: '#2563eb', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.fileName || 'Unnamed File'}
                                  </a>
                                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    <span style={{ fontWeight: '600' }}>By {file.uploadedBy}</span> • {new Date(file.uploadedAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                    <FiAlertCircle size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
                    <p>No records found for this patient</p>
                  </div>
                )}
              </div>

              </>)}

            </div>

            <div className="ma-modal-footer" style={{ display: 'flex', gap: '12px' }}>
              <button
                className="ma-cancel-btn"
                onClick={() => {
                  setShowViewDataModal(false);
                  setPatientRecords(allPatientAppointments.map(appointment => ({
                    ...appointment,
                    attachmentNotes: appointment.displayNotes || appointment.attachmentNotes || []
                  })));
                  setShowRecordsModal(true);
                }}
                style={{
                  flex: 1,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  color: '#374151',
                  fontWeight: '500',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiPaperclip size={16} />
                View Attachments
              </button>
              <button
                onClick={handleTerminateAccess}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  background: isSubmitting ? '#fecaca' : '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                  fontWeight: '600',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {isSubmitting ? 'Terminating...' : 'Terminate Access'}
              </button>
            </div>

          </div>

        </div>,

        document.body

      )}

      {/* RECORDS MODAL */}
      {viewing && showRecordsModal && createPortal(
        <div className="ma-modal-backdrop">
          <div className="ma-modal-card wide" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <button
              className="ma-modal-close"
              type="button"
              onClick={() => {
                setShowRecordsModal(false);
                setViewing(null);
              }}
            >
              <FiX />
            </button>
            <h3>Patient Records - {viewing.patientName}</h3>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              All attachments and findings made by medical professionals for this patient
            </p>

            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '12px' }}>
              {loadingRecords ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                  <div style={{ width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
                  <p>Loading records...</p>
                </div>
              ) : patientRecords && patientRecords.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {patientRecords.map((apt) => {
                    const notes = apt.attachmentNotes || [];
                    const attachments = apt.attachments || [];

                    if (notes.length === 0 && attachments.length === 0) return null;

                    return (
                      <div key={apt.id} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0' }}>
                        <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                          <span>{apt.serviceName} - {apt.date} • {apt.time}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '500', color: '#64748b', textAlign: 'right', overflowWrap: 'anywhere' }}>
                            Reference #: {apt.reference || 'N/A'}
                          </span>
                        </h4>

                        {notes.length > 0 && (
                          <div style={{ marginBottom: '16px' }}>
                            <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Findings</h5>
                            {notes.map((note, idx) => (
                              <div key={idx} style={{ padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                <p style={{ margin: 0, fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                                  <span style={{ fontWeight: '600' }}>By {note.createdBy}</span> • {new Date(note.createdAt).toLocaleDateString()} • {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {attachments.length > 0 && (
                          <div>
                            <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Uploads</h5>
                            {attachments.map((file, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                                  {file.fileType?.includes('image') ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                  ) : file.fileType?.includes('pdf') ? (
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                  ) : (
                                    <FiPaperclip size={16} />
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: '600', color: '#2563eb', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.fileName || 'Unnamed File'}
                                  </a>
                                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    <span style={{ fontWeight: '600' }}>By {file.uploadedBy}</span> • {new Date(file.uploadedAt).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                  <FiFileText size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
                  <p>No records found for this patient</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* BLOCKCHAIN MODAL */}

      {viewing && showBlockchainModal && createPortal(

        <div className="ma-modal-backdrop">

          <div className="ma-modal-card" style={{ maxWidth: '600px' }}>

            <button

              className="ma-modal-close"

              type="button"

              onClick={() => {

                setShowBlockchainModal(false);

                setViewing(null);

              }}

            >

              <FiX />

            </button>

            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

              <FiShield size={20} color="#2563eb" />

              Blockchain Verification

            </h3>

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>

              {/* Tamper Warning */}

              {viewing.blockchain.status === 'verified' && (

                <div style={{ marginBottom: '16px', padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px' }}>

                  <FiAlertCircle size={16} color="#dc2626" />

                  <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '500' }}>

                    This record is blockchain-verified. Any modifications will be detected as tampering.

                  </span>

                </div>

              )}

              <div className="ma-modal-details" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>

                <div className="ma-detail-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>

                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Status</div>

                  <div style={{ fontSize: '14px', fontWeight: '600' }}>

                    <span 

                      style={{

                        fontSize: '12px',

                        padding: '4px 8px',

                        borderRadius: '999px',

                        fontWeight: '600',

                        textTransform: 'uppercase',

                        letterSpacing: '0.05em',

                        ...(viewing.blockchain.status === 'verified' ? {

                          background: '#ecfdf5',

                          color: '#059669',

                          border: '1px solid #10b981'

                        } : viewing.blockchain.status === 'pending' ? {

                          background: '#fef3c7',

                          color: '#d97706',

                          border: '1px solid #f59e0b'

                        } : {

                          background: '#f3f4f6',

                          color: '#6b7280',

                          border: '1px solid #9ca3af'

                        })

                      }}

                    >

                      {viewing.blockchain.status === 'verified' && <FiShield size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}

                      {viewing.blockchain.status}

                    </span>

                  </div>

                </div>

                {viewing.blockchain.network && (

                  <div className="ma-detail-row" style={{ display: 'block', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>

                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Network</div>

                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{viewing.blockchain.network}</div>

                  </div>

                )}

                {viewing.blockchain.verifiedAt && (

                  <div className="ma-detail-row" style={{ display: 'block', padding: '8px 0' }}>

                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Verified At</div>

                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{new Date(viewing.blockchain.verifiedAt).toLocaleString()}</div>

                  </div>

                )}

              </div>

            </div>

          </div>

        </div>,

        document.body

      )}



      {/* ATTACHMENTS MODAL */}

      {viewing && showAttachmentsModal && createPortal(

        <div className="ma-modal-backdrop">

          <div className="ma-modal-card wide" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

            <button

              className="ma-modal-close"

              type="button"

              onClick={() => {
                setShowAttachmentsModal(false);
                setViewing(null);
                setClinicalNote("");
                setAttachmentFiles([]);
                setExistingNotes([]);
                setRemovedAttachmentIds([]);
                setSelectedNoteId("");
                setShowAttachmentEditor(false);
                setShowDeleteConfirm(false);
                setNoteError("");
                setNdaAgreed(false);
              }}

            >

              <FiX />

            </button>

            <h3>Appointment Attachments & Findings</h3>

            

            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '4px' }}>

              {/* Expiry banner — only for COMPLETED appointments */}
              {(() => {
                if (viewing?.status !== 'COMPLETED') return null;
                const completedAt = viewing.completedAt?.toDate
                  ? viewing.completedAt.toDate()
                  : viewing.completedAt ? new Date(viewing.completedAt) : null;
                const expiry = viewing.editDeadline
                  ? new Date(viewing.editDeadline)
                  : completedAt
                    ? new Date(completedAt.getTime() + 7 * 24 * 60 * 60 * 1000)
                    : null;
                const isExpired = expiry ? expiry <= new Date() : false;
                return (
                  <div style={{
                    margin: '0 0 20px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${isExpired ? '#fecaca' : '#bfdbfe'}`,
                    background: isExpired ? '#fef2f2' : '#eff6ff',
                    color: isExpired ? '#991b1b' : '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '13px'
                  }}>
                    <FiClock size={18} />
                    <span>
                      <strong>Attachment availability:</strong>{' '}
                      {expiry
                        ? `${isExpired ? 'Expired on' : 'Available until'} ${expiry.toLocaleString()}`
                        : 'Expiry date unavailable'}
                    </span>
                  </div>
                );
              })()}

            <div className="ma-modal-details" style={{ marginBottom: '24px' }}>

              <div className="ma-detail-row">

                <span className="ma-detail-label">Patient</span>

                <span className="ma-detail-value">{viewing?.patientName}</span>

              </div>

              <div className="ma-detail-row">

                <span className="ma-detail-label">Reference #</span>

                <span className="ma-detail-value">{viewing?.reference}</span>

              </div>

            </div>



            {/* New-entry form — hidden when editor is open */}
            {!showAttachmentEditor && (
            <div className="ma-form-field">

              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Clinical Findings / Notes *</label>

              <textarea 

                className="ma-textarea" 

                placeholder="Enter clinical findings, consultation summaries, laboratory interpretations, prescribed recommendations, follow-up instructions, or relevant patient care notes." 

                value={clinicalNote} 

                onChange={(e) => setClinicalNote(e.target.value)}

                style={{ height: '120px', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '12px', width: '100%', resize: 'none' }}

              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>

                <span style={{ fontSize: '11px', color: noteError ? '#dc2626' : '#6b7280' }}>

                  {noteError || "Min 10, Max 500 characters"}

                </span>

                <span style={{ fontSize: '11px', color: clinicalNote.length > 500 ? '#dc2626' : '#6b7280' }}>

                  {clinicalNote.length}/500

                </span>

              </div>

            </div>
            )}



            {/* Add Attachments file picker — hidden when editor is open */}
            {!showAttachmentEditor && (
            <div className="ma-form-field" style={{ marginTop: '20px' }}>

              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Add Attachments</label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                  <input 

                    type="file" 

                    id="clinical-attachment" 

                    style={{ display: 'none' }} 

                    multiple 

                    onChange={(e) => {

                      if (e.target.files) {

                        const newFiles = Array.from(e.target.files);

                        setAttachmentFiles(prev => [...prev, ...newFiles]);

                      }

                    }}

                    accept=".pdf,.png,.jpg,.jpeg"

                  />

                  <label 

                    htmlFor="clinical-attachment" 

                    style={{ 

                      cursor: 'pointer', 

                      display: 'flex', 

                      alignItems: 'center', 

                      gap: '8px', 

                      padding: '10px 16px', 

                      borderRadius: '8px', 

                      background: '#f3f4f6', 

                      border: '1px solid #e5e7eb',

                      transition: 'all 0.2s'

                    }}

                  >

                    <FiPaperclip size={18} />

                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Select Files</span>

                  </label>

                </div>



                {attachmentFiles.length > 0 && (

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>

                    {attachmentFiles.map((file, idx) => (

                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#ecfdf5', borderRadius: '16px', border: '1px solid #10b981' }}>

                        <FiFileText size={14} color="#059669" />

                        <span style={{ fontSize: '12px', color: '#065f46', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>

                        <button onClick={() => setAttachmentFiles(prev => prev.filter((_, i) => i !== idx))} style={{ border: 'none', background: 'none', color: '#059669', cursor: 'pointer', display: 'flex' }}><FiX size={14} /></button>

                      </div>

                    ))}

                  </div>

                )}

              </div>

              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>Supported: PDF, PNG, JPG, JPEG (Multiple files allowed)</p>

              {/* NDA Agreement Checkbox */}
              <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="nda-agree"
                  checked={ndaAgreed}
                  onChange={(e) => setNdaAgreed(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0, accentColor: '#2563eb', width: '15px', height: '15px', cursor: 'pointer' }}
                />
                <label htmlFor="nda-agree" style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5', cursor: 'pointer' }}>
                  I have read and agree to comply with the{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/AuraVit_Medical_Professional_Confidentiality_NDA.pdf', '_blank')}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Medical Professional Confidentiality and Non-Disclosure Agreement
                  </button>
                  .
                </label>
              </div>

            </div>
            )}



            <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f3f4f6' }}>

              {/* ── EDIT MODE BLOCK ── */}
              {isAppointmentEditable(viewing) && showAttachmentEditor ? (
                <div style={{ padding: '16px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', margin: 0 }}>Edit Consultation Summary & Files</h4>
                    <button type="button" onClick={() => { setShowAttachmentEditor(false); setClinicalNote(""); setSelectedNoteId(""); setAttachmentFiles([]); setRemovedAttachmentIds([]); setNdaAgreed(false); }} style={{ border: '1px solid #d1d5db', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: '600', padding: '4px 12px', borderRadius: '4px' }}>Cancel</button>
                  </div>

                  {existingNotes.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                        Finding to edit
                        <select
                          value={selectedNoteId}
                          onChange={(e) => {
                            const nextId = e.target.value;
                            setSelectedNoteId(nextId);
                            setClinicalNote(existingNotes.find((n) => n.id === nextId)?.text || "");
                          }}
                          style={{ display: 'block', width: '100%', marginTop: '6px', padding: '9px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff' }}
                        >
                          <option value="">— select a finding —</option>
                          {existingNotes.map((note, index) => (
                            <option key={note.id || index} value={note.id}>
                              {index === existingNotes.length - 1 ? "Latest finding" : `Finding ${index + 1}`} — {new Date(note.createdAt).toLocaleString()}
                            </option>
                          ))}
                        </select>
                      </label>
                      {selectedNoteId && (
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={isUploading}
                          style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', borderRadius: '4px', padding: '6px 12px', fontSize: '12px', fontWeight: '600' }}
                        >
                          Delete This Finding
                        </button>
                      )}

                      {/* Delete confirmation portal */}
                      {showDeleteConfirm && createPortal(
                        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                          <div className="modal-content" style={{ maxWidth: '400px', padding: '24px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Delete Finding?</h3>
                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                              Are you sure you want to delete this finding? This action cannot be undone.
                            </p>
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(false)}
                                style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={isUploading}
                                onClick={async () => {
                                  if (!selectedNoteId) return;
                                  try {
                                    setIsUploading(true);
                                    const noteToRemove = existingNotes.find(n => n.id === selectedNoteId);
                                    if (!noteToRemove) throw new Error("Finding not found");
                                    await updateDoc(doc(db, "appointments", viewing.id), {
                                      attachmentNotes: arrayRemove(noteToRemove)
                                    });
                                    setNotificationMessage("Finding deleted successfully!");
                                    setNotificationType("success");
                                    setSelectedNoteId("");
                                    setClinicalNote("");
                                    setShowDeleteConfirm(false);
                                    delete appointmentDataCache.current[viewing.id];
                                    lastFetchedAppointmentId.current = null;
                                    const apptDoc = await getDoc(doc(db, "appointments", viewing.id));
                                    if (apptDoc.exists()) {
                                      const data = apptDoc.data();
                                      appointmentDataCache.current[viewing.id] = data;
                                      lastFetchedAppointmentId.current = viewing.id;
                                      setExistingNotes(data.attachmentNotes || []);
                                      setViewing(prev => ({ ...prev, attachments: data.attachments || [], attachmentNotes: data.attachmentNotes || [] }));
                                    }
                                  } catch (err) {
                                    console.error("Error deleting finding:", err);
                                    setNoteError(err.message || "Failed to delete finding. Please try again.");
                                  } finally {
                                    setIsUploading(false);
                                  }
                                }}
                                style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                              >
                                {isUploading ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                    </div>
                  )}

                  <textarea
                    className="ma-textarea"
                    placeholder="Enter consultation findings or summary (minimum 10 characters)."
                    value={clinicalNote}
                    onChange={(e) => setClinicalNote(e.target.value)}
                    style={{ height: '100px', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '12px', width: '100%', resize: 'none' }}
                  />

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="file"
                        id="ar-edit-attachment"
                        style={{ display: 'none' }}
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => setAttachmentFiles(e.target.files ? Array.from(e.target.files) : [])}
                      />
                      <label htmlFor="ar-edit-attachment" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 14px', borderRadius: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb' }}>
                        <FiPaperclip size={16} /> Select Files
                      </label>
                    </div>
                    <button
                      className="ma-primary-btn"
                      onClick={handleAttachmentUpload}
                      disabled={isUploading || !ndaAgreed || (!clinicalNote.trim() && attachmentFiles.length === 0 && removedAttachmentIds.length === 0)}
                    >
                      {isUploading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                  {/* NDA Agreement Checkbox in Edit Mode */}
                  <div style={{ marginTop: '12px', padding: '12px 14px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <input
                      type="checkbox"
                      id="ar-edit-nda-agree"
                      checked={ndaAgreed}
                      onChange={(e) => setNdaAgreed(e.target.checked)}
                      style={{ marginTop: '2px', flexShrink: 0, accentColor: '#2563eb', width: '15px', height: '15px', cursor: 'pointer' }}
                    />
                    <label htmlFor="ar-edit-nda-agree" style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5', cursor: 'pointer' }}>
                      I have read and agree to comply with the{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/AuraVit_Medical_Professional_Confidentiality_NDA.pdf', '_blank')}
                        style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Medical Professional Confidentiality and Non-Disclosure Agreement
                      </button>
                      .
                    </label>
                  </div>

                  {/* Existing files with remove buttons */}
                  {viewing?.attachments?.length > 0 && (
                    <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', background: '#fff', border: '1px solid #e5e7eb' }}>
                      <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '0 0 10px 0' }}>Existing Uploaded Files</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                        {viewing.attachments.filter(f => !removedAttachmentIds.includes(f.id)).map((file, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                            <FiPaperclip color="#6b7280" size={14} />
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.fileName || 'Unnamed File'}
                              </a>
                              <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(file.uploadedAt).toLocaleDateString()} by {file.uploadedBy}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setRemovedAttachmentIds(prev => [...new Set([...prev, file.id])])}
                              title={`Remove ${file.fileName || 'document'}`}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px', borderRadius: '4px' }}
                              aria-label={`Remove ${file.fileName || 'document'}`}
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <p style={{ margin: '12px 0 0', fontSize: '11px', color: '#6b7280' }}>
                    Remove existing documents above or add new files before saving.
                  </p>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: noteError ? '#dc2626' : '#6b7280' }}>
                    {noteError || `${attachmentFiles.length} file(s) selected to add • ${removedAttachmentIds.length} file(s) marked for removal`}
                  </div>
                </div>

              ) : !isAppointmentEditable(viewing) ? (
                <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '13px' }}>
                  Editing is disabled because the record is finalized or the 7-day editing window has expired.
                </div>
              ) : (
                <div style={{ marginBottom: '20px', textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => setShowAttachmentEditor(true)}
                    style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: '600' }}
                  >
                    Edit Consultation Summary & Files
                  </button>
                </div>
              )}

              {/* Two-column read-only view — hidden when editor is open */}
              {!showAttachmentEditor && (
              <div style={{ display: 'flex', gap: '24px' }}>

                {/* Left: Existing Notes */}

                <div style={{ flex: '0 0 50%', minWidth: 0 }}>

                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Consultation Findings</h4>

                  {existingNotes.length > 0 ? (

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>

                      {existingNotes.map((note, idx) => (

                        <div key={idx} style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>

                          <p style={{ margin: 0, fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{note.text}</p>

                          <div style={{ marginTop: '6px', fontSize: '10px', color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>

                            <span>By {note.createdBy}</span>

                            <span>{new Date(note.createdAt).toLocaleDateString()}</span>

                          </div>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No findings recorded yet.</p>

                  )}

                </div>



                {/* Right: Existing Attachments */}

                <div style={{ flex: '0 0 calc(50% - 24px)', minWidth: 0 }}>

                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>Uploaded Documents</h4>

                  {viewing?.attachments?.filter(f => !removedAttachmentIds.includes(f.id)).length > 0 ? (

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '250px', overflowY: 'auto', paddingRight: '8px' }}>

                      {viewing.attachments.filter(f => !removedAttachmentIds.includes(f.id)).map((file, index) => (

                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff' }}>

                          <FiPaperclip color="#6b7280" />

                          <div style={{ flex: 1, overflow: 'hidden' }}>

                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                              {file.fileName || 'Unnamed File'}

                            </a>

                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>{new Date(file.uploadedAt).toLocaleDateString()} by {file.uploadedBy}</span>

                          </div>

                        </div>

                      ))}

                    </div>

                  ) : (

                    <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No files found.</p>

                  )}

                </div>

              </div>
              )}

            </div>

            </div>



            {/* Footer — hidden when editor is open */}
            {!showAttachmentEditor && (
            <div className="ma-modal-footer" style={{ marginTop: '32px', flexShrink: 0 }}>

              <button 

                className="ma-modal-btn secondary" 

                onClick={() => {

                  setShowAttachmentsModal(false);

                  setViewing(null);

                  setClinicalNote("");

                  setAttachmentFiles([]);

                  setRemovedAttachmentIds([]);

                  setSelectedNoteId("");

                  setShowAttachmentEditor(false);

                  setShowDeleteConfirm(false);

                  setNdaAgreed(false);

                }}

              >

                Cancel

              </button>

              <button

                className="ma-modal-btn primary"

                onClick={handleAttachmentUpload}

                disabled={isUploading || !ndaAgreed || (!clinicalNote.trim() && attachmentFiles.length === 0)}

                style={{ minWidth: '160px' }}

              >

                {isUploading ? "Saving..." : "Save Findings & Upload"}

              </button>

            </div>
            )}

          </div>

        </div>,

        document.body

      )}

    </div>

  );

};



export default AdminRecords;
